import { useState, useEffect, useMemo } from 'react';
import { useExchangeRates } from './useExchangeRates';
import type { InvestmentAccount } from '@/types/investments';

const SESSION_STORAGE_KEY = 'selected-investment-currency';

export interface InvestmentAccountWithConvertedBalance extends InvestmentAccount {
  convertedBalance: number;
  conversionRate: number;
}

export interface UseInvestmentCurrencyConversionOptions {
  baseCurrency: string;
  investments: InvestmentAccount[];
}

export interface UseInvestmentCurrencyConversionReturn {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  convertedInvestments: InvestmentAccountWithConvertedBalance[];
  totalNetWorth: number;
  isConverting: boolean;
  conversionError: Error | null;
}

/**
 * Custom hook for managing currency conversion for investment accounts
 * 
 * Features:
 * - Manages selected currency state with sessionStorage persistence
 * - Converts all investment balances to selected currency
 * - Calculates total net worth in selected currency
 * - Handles conversion errors with automatic fallback to base currency
 * - Provides loading states during conversion
 */
export function useInvestmentCurrencyConversion(
  options: UseInvestmentCurrencyConversionOptions
): UseInvestmentCurrencyConversionReturn {
  const { baseCurrency, investments } = options;

  // Initialize selected currency from session storage or default to base currency
  const [selectedCurrency, setSelectedCurrencyState] = useState<string>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return stored || baseCurrency;
    } catch (error) {
      console.warn('Failed to load selected currency from session storage:', error);
      return baseCurrency;
    }
  });

  // Track previous currency for error recovery
  const [previousCurrency, setPreviousCurrency] = useState<string>(selectedCurrency);

  // Fetch exchange rates for the selected currency
  const {
    rates,
    isLoading: isLoadingRates,
    error: ratesError,
    convert,
  } = useExchangeRates(selectedCurrency);

  // Persist selected currency to session storage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, selectedCurrency);
    } catch (error) {
      console.warn('Failed to save selected currency to session storage:', error);
    }
  }, [selectedCurrency]);

  // Handle currency selection with error recovery
  const setSelectedCurrency = (currency: string) => {
    setPreviousCurrency(selectedCurrency);
    setSelectedCurrencyState(currency);
  };

  // Handle conversion errors - revert to previous currency
  useEffect(() => {
    if (ratesError && selectedCurrency !== previousCurrency) {
      console.error('Currency conversion failed, reverting to previous currency:', ratesError);
      setSelectedCurrencyState(previousCurrency);
    }
  }, [ratesError, selectedCurrency, previousCurrency]);

  // Convert all investment balances to selected currency
  const convertedInvestments = useMemo<InvestmentAccountWithConvertedBalance[]>(() => {
    if (!rates || isLoadingRates) {
      // Return investments with original balances during loading
      return investments.map(investment => ({
        ...investment,
        convertedBalance: investment.balance,
        conversionRate: 1,
      }));
    }

    try {
      return investments.map(investment => {
        // If investment currency matches selected currency, no conversion needed
        if (investment.currency === selectedCurrency) {
          return {
            ...investment,
            convertedBalance: investment.balance,
            conversionRate: 1,
          };
        }

        // Convert balance to selected currency
        try {
          const convertedBalance = convert(investment.balance, investment.currency, selectedCurrency);
          const conversionRate = convertedBalance / investment.balance;

          return {
            ...investment,
            convertedBalance,
            conversionRate,
          };
        } catch (conversionError) {
          console.error(
            `Failed to convert ${investment.fund_name} from ${investment.currency} to ${selectedCurrency}:`,
            conversionError
          );
          // Fallback to original balance if conversion fails for this investment
          return {
            ...investment,
            convertedBalance: investment.balance,
            conversionRate: 1,
          };
        }
      });
    } catch (error) {
      console.error('Error during investment conversion:', error);
      // Return investments with original balances on error
      return investments.map(investment => ({
        ...investment,
        convertedBalance: investment.balance,
        conversionRate: 1,
      }));
    }
  }, [investments, selectedCurrency, rates, isLoadingRates, convert]);

  // Calculate total net worth in selected currency
  const totalNetWorth = useMemo(() => {
    return convertedInvestments.reduce((total, investment) => {
      return total + investment.convertedBalance;
    }, 0);
  }, [convertedInvestments]);

  // Determine if conversion is in progress
  const isConverting = isLoadingRates;

  // Determine conversion error state
  const conversionError = ratesError;

  return {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  };
}
